import { useEffect, useState, createContext, useContext, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
import storage from 'utils/storage';
import user_api from 'utils/api/users';
import Bugsnag from '@bugsnag/js';
import { Snackbar } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import authDebugger from 'utils/authDebugger';

// Create Authentication Context
const AuthContext = createContext();

// Hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a SessionAuth provider');
  }
  return context;
};

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

// Session cache to avoid redundant checks
let sessionCache = {
  token: null,
  userData: null,
  lastCheck: 0,
  isValid: false,
  checkInProgress: false
};

// Cache duration (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;
const TOKEN_REFRESH_THRESHOLD = 30 * 60 * 1000; // Refresh when 30 min left

export default function SessionAuth({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [checked, setChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const sessionCheckRef = useRef(null);
  const mountedRef = useRef(true);

  // Snackbar handlers
  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      // Clear all storage
      await storage.remove();
      
      // Reset session cache completely
      sessionCache = { 
        token: null, 
        userData: null, 
        lastCheck: 0, 
        isValid: false, 
        checkInProgress: false 
      };
      
      // Reset component state
      setIsAuthenticated(false);
      setUserData(null);
      setChecked(true);
      setIsLoading(false);
      
      // Hide root loader
      if (window.hideRootLoader && typeof window.hideRootLoader === 'function') {
        try {
          window.hideRootLoader();
        } catch (error) {
          console.warn('Error hiding root loader:', error);
        }
      }
      
      // Clear any user data cache if available
      if (window.userDataCache && typeof window.userDataCache.clear === 'function') {
        window.userDataCache.clear();
      }
      
      // Notify other tabs about logout
      try {
        localStorage.setItem('auth_logout', Date.now().toString());
        localStorage.removeItem('auth_logout');
      } catch (storageError) {
        console.warn('Could not notify other tabs about logout:', storageError);
      }
      
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Force navigate to login even if there are errors
      navigate('/login');
    }
  }, [navigate]);

  // Enhanced token validation with refresh capability
  const validateAndRefreshToken = useCallback(async (token) => {
    try {
      const decodedToken = jwt_decode(token);
      const currentTime = Date.now();
      const tokenExpiry = decodedToken.exp * 1000;
      const timeUntilExpiry = tokenExpiry - currentTime;

      // Check if token is already expired
      if (timeUntilExpiry <= 0) {
        
        // Try to refresh the token by re-authenticating with stored credentials
        const rememberMe = storage.getRememberMe();
        if (rememberMe) {
          try {
            // Note: This would require a refresh endpoint on the backend
            // For now, we'll use the existing login flow
            return { isValid: false, shouldLogout: true };
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            return { isValid: false, shouldLogout: true };
          }
        }
        
        return { isValid: false, shouldLogout: true };
      }

      // Check if token needs refresh soon (30 minutes before expiry)
      if (timeUntilExpiry < TOKEN_REFRESH_THRESHOLD) {
        // Could implement background refresh here
      }

      return { isValid: true, shouldLogout: false, token, decodedToken };
    } catch (error) {
      console.error('Token validation error:', error);
      return { isValid: false, shouldLogout: true };
    }
  }, []);

  // Optimized user data fetching with caching
  const fetchUserData = useCallback(async (userId, useCache = true) => {
    const now = Date.now();
    
    if (!userId) {
      console.error('fetchUserData: userId is null or undefined');
      return { success: false, error: 'User ID is required' };
    }
    
    // Return cached data if valid and recent
    if (useCache && sessionCache.userData && sessionCache.lastCheck && 
        (now - sessionCache.lastCheck < CACHE_DURATION)) {
      
      // Use debugger to validate cached data
      const validation = authDebugger.validateUserData(sessionCache.userData, 'cachedData');
      
      if (validation.valid) {
        return { success: true, userData: sessionCache.userData };
      } else {
        // Clear invalid cached data
        sessionCache.userData = null;
        sessionCache.lastCheck = 0;
      }
    }

    try {
      const result = await Promise.race([
        user_api.get_user(userId),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), 8000)
        )
      ]);

      if (!result) {
        throw new Error('API returned null or undefined result');
      }

      if (!result.ok) {
        let errorMessage = 'Failed to fetch user data.';
        try {
          const errorData = result.data || JSON.parse(result.text || '{}');
          errorMessage = errorData?.message || errorData?.error?.message || errorMessage;
        } catch (parseError) {
          // Continue with generic error message
        }
        throw new Error(errorMessage);
      }

      const userData = result.data;

      // Use debugger for comprehensive validation
      const validation = authDebugger.validateUserData(userData, 'freshAPIData');
      
      if (!validation.valid) {
        console.error('Invalid user data from API:', validation.issues.join(', '));
        throw new Error(`Invalid user data from API: ${validation.issues.join(', ')}`);
      }

      if (userData.isArchived) {
        return { success: false, isArchived: true };
      }

      // Update cache with validated data
      try {
        sessionCache.userData = { ...userData }; // Create a copy to avoid reference issues
        sessionCache.lastCheck = now;
      } catch (cacheError) {
        console.error('Error caching user data:', cacheError);
        // Continue without caching
      }

      return { success: true, userData };
    } catch (error) {
      console.error('User data fetch error:', error.message);
      
      // Clear any potentially corrupted cache
      sessionCache.userData = null;
      sessionCache.lastCheck = 0;
      
      return { success: false, error: error.message };
    }
  }, []);

  // Debounced session check to prevent rapid successive calls
  const performSessionCheck = useCallback(async () => {
    // Prevent multiple simultaneous checks
    if (sessionCache.checkInProgress) {
      return;
    }

    sessionCache.checkInProgress = true;
    setIsLoading(true);
    
    // Show root loader during session validation
    // if (window.showRootLoader) {
    //   window.showRootLoader('Verifying your session...');
    // }

    try {
      const path = location.pathname;
      const publicPaths = [
        '/login', '/store', '/store/create', '/store/onboard',
        '/404', '/undefined', '/track-time', '/group/create', '/register', '/archived'
      ];

      const isEmployeePublicPath = params.store && 
        (path === `/${params.store}/register` || path === `/${params.store}/onboard`);
      
      const employeePathRegex = /^\/[a-f\d]{24}\/(register|onboard)$/;
      const isEmployeePathByRegex = employeePathRegex.test(path);
      
      const isPublic = publicPaths.includes(path) || isEmployeePublicPath || isEmployeePathByRegex;

      // Get token with timeout protection
      const token = await Promise.race([
        storage.getToken(),
        new Promise((resolve) => setTimeout(() => resolve(null), 3000))
      ]);

      if (!token) {
        setIsAuthenticated(false);
        setChecked(true);
        setIsLoading(false);
        
        // Hide root loader
        if (window.hideRootLoader) {
          window.hideRootLoader();
        }
        
        if (!isPublic) {
          await storage.remove();
          navigate('/login');
        }
        return;
      }

      // Validate and potentially refresh token
      const tokenValidation = await validateAndRefreshToken(token);
      
      if (!tokenValidation.isValid) {
        setIsAuthenticated(false);
        setChecked(true);
        setIsLoading(false);
        
        // Hide root loader
        if (window.hideRootLoader) {
          window.hideRootLoader();
        }
        
        await storage.remove();
        if (!isPublic) {
          if (tokenValidation.shouldLogout) {
            showSnackbar('Your session has expired. Please log in again.', 'warning');
          }
          navigate('/login');
        }
        return;
      }

      const { decodedToken } = tokenValidation;
      setIsAuthenticated(true);

      // Handle authenticated user navigation
      if (path === '/login' || path === '/store' || path === '/store/create' || path === '/store/onboard') {
        if (!isEmployeePublicPath) {
          // Will redirect after user data is fetched
        }
      }

      // Fetch user data with caching
      const userDataResult = await fetchUserData(decodedToken.id);
      
      if (!userDataResult) {
        console.error('fetchUserData returned null or undefined');
        setIsAuthenticated(false);
        showSnackbar('Failed to fetch user data. Please log in again.', 'error');
        await storage.remove();
        if (!isPublic) {
          navigate('/login');
          return;
        }
      }
      
      if (!userDataResult.success) {

        if (userDataResult.isArchived) {
          // Handle archived users - redirect to archived page and stay there until logout
          if (path !== '/archived') {
            setIsAuthenticated(false);

            // Redirect to archived page if not already there
            // Use decodedToken.id as fallback if userData is not available
            const userId = userDataResult.userData?._id || decodedToken.id;
            navigate('/archived', {
              state: {
                userId: userId,
                isArchived: true,
                role: userDataResult.userData?.role || 0,
                company: userDataResult.userData?.company || 'Unknown Company'
              }
            }, { replace: true })
            return;
          } else {
            // Already on archived page, allow access but provide user data for role checking
            setIsAuthenticated(false);
            // For archived users on archived page, we still need user data for role-based features
            if (userDataResult.userData) {
              setUserData({ ...userDataResult.userData, isArchived: true });
            } else {
              // Fallback: create minimal user data from decoded token
              setUserData({
                _id: decodedToken.id,
                isArchived: true,
                role: decodedToken.role || 0,
                company: decodedToken.company || 'Unknown Company',
                displayName: decodedToken.displayName || 'Archived User'
              });
            }
            setChecked(true);
            setIsLoading(false);

            // Hide root loader
            if (window.hideRootLoader) {
              window.hideRootLoader();
            }
            return; // Allow access to archived page
          }
        }

        setIsAuthenticated(false);
        showSnackbar(userDataResult.error || 'Could not verify your session. Please log in.', 'error');
        await storage.remove();
        if (!isPublic) {
          navigate('/login');
          return;
        }
        return; // Add return to prevent further execution
      }

      const userData = userDataResult.userData;
      
      // Use debugger to validate user data
      const validation = authDebugger.validateUserData(userData, 'sessionCheck-main');
      
      if (!validation.valid) {
        
        setIsAuthenticated(false);
        showSnackbar(`Invalid user data: ${validation.issues.join(', ')}. Please log in again.`, 'error');
        await storage.remove();
        sessionCache.userData = null;
        sessionCache.isValid = false;
        if (!isPublic) {
          navigate('/login');
          return;
        }
      }

      // Safe user data setting with debugger protection
      try {
        setUserData(userData ? { ...userData } : null);
      } catch (setUserDataError) {
        console.error('Error setting user data:', setUserDataError);
        
        setIsAuthenticated(false);
        showSnackbar('Failed to process user data. Please log in again.', 'error');
        await storage.remove();
        if (!isPublic) {
          navigate('/login');
          return;
        }
      }

      // Handle dashboard redirection for authenticated users (with extensive null checks)
      if (path === '/login' || path === '/store' || path === '/store/create' || path === '/store/onboard') {
        if (!isEmployeePublicPath) {
          try {
            
            // Use debugger's safe role access
            const userRole = authDebugger.safeGetUserRole(userData, 'dashboardRedirection', 0);
            
            // Use same logic as routes.js getDashboardPath()
            const dashboardPath = userRole === 0 ? '/dashboard/app' : '/stores/app';
            
            navigate(dashboardPath);
            return;
          } catch (redirectError) {
            console.error('Error during dashboard redirection:', redirectError);
            // Continue execution instead of failing completely
          }
        }
      }

      // Check if user is on wrong dashboard and redirect to correct one
      try {
        if (userData) {
          const userRole = authDebugger.safeGetUserRole(userData, 'dashboardValidation', 0);

          // Employee (role 0) should be on /dashboard/* paths
          // Admin/Store users (role >= 1) should be on /stores/* paths
          if (userRole === 0 && path.startsWith('/stores/')) {
            console.log('🔄 Employee (role 0) on admin dashboard, redirecting to employee dashboard');
            navigate('/dashboard/app');
            return;
          } else if (userRole >= 1 && path.startsWith('/dashboard/')) {
            console.log('🔄 Admin/Store user (role >= 1) on employee dashboard, redirecting to admin dashboard');
            navigate('/stores/app');
            return;
          }
        }
      } catch (roleCheckError) {
        console.error('Error during role-based dashboard validation:', roleCheckError);
        // Continue execution - don't break the app
      }

      // Handle archived user navigation
      if (userData && userData.isArchived) {
        // Archived users should only be on the archived page
        if (path !== '/archived') {
          console.log('🔄 Archived user on non-archived page, redirecting to archived page');
          navigate('/archived', {
            state: {
              userId: userData._id || decodedToken.id,
              isArchived: true,
              role: userData.role || 0,
              company: userData.company || 'Unknown Company'
            }
          }, { replace: true });
          return;
        }
        // If already on archived page, allow access
      } else if (userData && !userData.isArchived) {
        // Non-archived users should not be on archived page
        if (path === '/archived') {
          console.log('🔄 Non-archived user on archived page, redirecting to appropriate dashboard');
          const userRole = authDebugger.safeGetUserRole(userData, 'archivedPageCheck', 0);
          const dashboardPath = userRole === 0 ? '/dashboard/app' : '/stores/app';
          navigate(dashboardPath, { replace: true });
          return;
        }
      } else if (!userData) {
        // If we don't have userData but we're not on a public path, redirect to login
        const publicPaths = [
          '/login', '/store', '/store/create', '/store/onboard',
          '/404', '/undefined', '/track-time', '/group/create', '/register', '/archived'
        ];

        const isEmployeePublicPath = params.store &&
          (path === `/${params.store}/register` || path === `/${params.store}/onboard`);

        const employeePathRegex = /^\/[a-f\d]{24}\/(register|onboard)$/;
        const isEmployeePathByRegex = employeePathRegex.test(path);

        const isPublic = publicPaths.includes(path) || isEmployeePublicPath || isEmployeePathByRegex;

        if (!isPublic) {
          console.log('🔄 No user data available, redirecting to login');
          navigate('/login', { replace: true });
          return;
        }
      }

      // Update session cache
      sessionCache.token = token;
      sessionCache.isValid = true;
      sessionCache.lastCheck = Date.now();

    } catch (error) {
      console.error('Session check error occurred:', error);
      Bugsnag.notify(error);
      setIsAuthenticated(false);
      showSnackbar('An authentication error occurred. Please try logging in again.', 'error');
      await storage.remove();
      
        // Hide root loader on error
        if (window.hideRootLoader) {
          try {
            window.hideRootLoader();
          } catch (error) {
            console.warn('Error hiding root loader:', error);
          }
        }
      
      const employeePathRegex = /^\/[a-f\d]{24}\/(register|onboard)$/;
      const isEmployeePathByRegex = employeePathRegex.test(location.pathname);
      const isEmployeePublicPath = params.store && 
        (location.pathname.endsWith('/register') || location.pathname.endsWith('/onboard'));
      
      if (!['/login', '/404', '/undefined', '/track-time'].includes(location.pathname) && 
          !isEmployeePublicPath && !isEmployeePathByRegex) {
        navigate('/login');
      }
    } finally {
      sessionCache.checkInProgress = false;
      if (mountedRef.current) {
        setChecked(true);
        setIsLoading(false);

        // Hide root loader when session check is complete
        if (window.hideRootLoader && typeof window.hideRootLoader === 'function') {
          try {
            window.hideRootLoader();
          } catch (error) {
            console.warn('Error hiding root loader:', error);
          }
        }
      } else {
        // Component is unmounting, don't call window.hideRootLoader
        console.log('SessionAuth component unmounting, skipping root loader hide');
      }
    }
  }, [location.pathname, navigate, params.store, validateAndRefreshToken, fetchUserData, showSnackbar]);

  // Cross-tab session synchronization
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'auth_logout') {
        // Another tab logged out, sync this tab
        setIsAuthenticated(false);
        setUserData(null);
        sessionCache = { token: null, userData: null, lastCheck: 0, isValid: false, checkInProgress: false };

        // Hide root loader
        if (window && window.hideRootLoader && typeof window.hideRootLoader === 'function') {
          try {
            window.hideRootLoader();
          } catch (error) {
            console.warn('Error hiding root loader:', error);
          }
        }

        // Always redirect to login on logout, regardless of current path
        navigate('/login', { replace: true });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [navigate]);

  // Initialize and clean up invalid cached data on mount
  useEffect(() => {
    // Clean up any invalid cached user data from previous sessions
    if (window.userDataCache && typeof window.userDataCache.validateAndCleanUserData === 'function') {
      window.userDataCache.validateAndCleanUserData();
    }
  }, []);

  // Main session check effect with debouncing
  useEffect(() => {
    // Clear any existing timeout
    if (sessionCheckRef.current) {
      clearTimeout(sessionCheckRef.current);
    }

    // Debounce session checks (300ms delay)
    sessionCheckRef.current = setTimeout(() => {
      performSessionCheck();
    }, 300);

    return () => {
      if (sessionCheckRef.current) {
        clearTimeout(sessionCheckRef.current);
      }
    };
  }, [performSessionCheck]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    isAuthenticated,
    userData,
    isLoading,
    logout,
    showSnackbar,
    refreshUserData: () => fetchUserData(userData?._id, false) // Force refresh
  }), [isAuthenticated, userData, isLoading, logout, showSnackbar, fetchUserData]);

  if (!checked) {
    return null; // Or a loading spinner
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
} 